<template>
  <div
    class="tw-relative tw-text-dorian-gray-500 tw-w-full"
    ref="target"
    :class="className"
  >
    <button
      @click="toggleIsOpen"
      class="tw-justify-between tw-w-full tw-items-center tw-py-2 tw-px-3 tw-flex tw-rounded tw-border tw-border-dorian-gray-100 tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all"
    >
      <span class="tw-truncate">
        {{ selectedValue ? selectedValue.text : placeholder }}
      </span>
      <ClIconTablerX
        v-if="selectedValue"
        @click.stop="onClearFilter"
        :width="'18px'"
        :height="'18px'"
      />
      <ClIconTablerChevronDown
        v-else
        class="tw-min-w-[16px] tw-transition-transform tw-text-dorian-gray-300"
        :width="'18px'"
        :height="'18px'"
        :class="chevronIconClass"
      />
    </button>
    <div
      class="tw-bg-white tw-outline-t-0 tw-absolute tw-right-0 tw-z-10 tw-flex tw-w-full tw-flex-col tw-rounded-b-md tw-border tw-border-dorian-gray-100 tw-shadow tw-transition-opacity"
      v-if="isOpen"
    >
      <div class="tw-relative">
        <input
          type="text"
          v-model="searchTerm"
          class="tw-block tw-w-full tw-rounded tw-border tw-bg-white tw-text-dorian-gray-500 tw-outline-none tw-ring-offset-1 tw-transition-all tw-text-base tw-py-2 tw-pl-9 tw-pr-4 tw-border-dorian-gray-100/80 focus:tw-border-secondary-500 focus:tw-ring-4 focus:tw-ring-secondary-500/10 disabled:tw-cursor-not-allowed disabled:tw-bg-dorian-gray-50 disabled:tw-text-dorian-gray-500/50 disabled:tw-outline-none read-only:tw-cursor-default read-only:tw-bg-dorian-gray-50 read-only:tw-text-dorian-gray-500/50 read-only:tw-outline-none"
          placeholder="Search"
          @click.stop
        />

        <div
          class="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-left-0 tw-flex tw-items-center tw-pl-3"
        >
          <ClIconTablerSearch
            :width="'16px'"
            :height="'16px'"
            class="tw-text-dorian-gray-200"
          />
        </div>
      </div>
      <ul ref="list" class="tw-max-h-40 tw-overflow-y-auto">
        <li
          v-for="option in options"
          :key="option.value"
          class="tw-py-2 tw-px-3 tw-cursor-pointer hover:tw-bg-secondary-50 tw-truncate"
          @click="onOptionClick(option)"
        >
          {{ option.text }}
        </li>
        <li v-if="options.length === 0" class="tw-py-2 tw-px-3">
          {{ noResults }}
        </li>
        <li v-if="isFetching" class="tw-text-center">
          <ClSpinner :width="'20px'" :height="'20px'" />
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, computed } from "vue";
import { onClickOutside, useInfiniteScroll } from "@vueuse/core";
import type { Option } from "./types";
import {
  ClIconTablerX,
  ClIconTablerSearch,
  ClIconTablerChevronDown,
} from "../icons";
import ClSpinner from "../spinner/cl-spinner.vue";

interface IProps {
  options: { text: string; value: string }[];
  isFetching: boolean;
  hasMore: boolean;
  placeholder: string;
  noResults: string;
  className?: string;
}

const props = defineProps<IProps>();
const emit = defineEmits<{
  (event: "load-more"): void;
}>();

const target = ref(null);
const list = ref<HTMLElement | null>(null);
const isOpen = ref<boolean>(false);
const selectedValue = defineModel<Option | null>("value");
const searchTerm = defineModel<string>("search");

const toggleIsOpen = () => {
  isOpen.value = !isOpen.value;
};

const chevronIconClass = computed(() =>
  isOpen.value ? "tw-rotate-180" : "tw-rotate-0",
);

onClickOutside(target, () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
});

const onClearFilter = () => {
  selectedValue.value = null;
};

const onOptionClick = (option: Option) => {
  selectedValue.value = option;
  searchTerm.value = "";
  isOpen.value = false;
};

useInfiniteScroll(
  list,
  () => {
    emit("load-more");
  },
  {
    distance: 5,
    canLoadMore: () => {
      return props.hasMore;
    },
  },
);
</script>
